import { documentBody, type MarkdownDocument } from '@markbird/shared';
import { db } from './db';
import { effectiveTags } from './metadata';
import { SearchIndex, type IndexHit, type IndexedDoc } from './searchIndex';

/**
 * Building and querying the search index, wherever it runs.
 *
 * Normally that is a Web Worker (search.worker.ts): the index build reads and
 * tokenizes every document, and on a large vault that is seconds of work the
 * main thread should not do while someone is trying to type. Where workers are
 * unavailable the same code runs in the page, which is also how the unit tests
 * exercise it.
 *
 * The indexer reads documents straight from IndexedDB, which a worker can open
 * as well as the page can — so a write only has to send an id across, never the
 * document.
 */

const CHUNK = 200;

/** What the index needs from a document. */
export function toIndexed(doc: MarkdownDocument): IndexedDoc {
  return {
    id: doc.id,
    title: doc.title,
    // Frontmatter is metadata: it is matched through `tag:`, not as prose.
    body: documentBody(doc.content),
    tags: effectiveTags(doc),
    folder: doc.folder ?? null,
    updatedAt: doc.updatedAt,
    pinned: doc.pinned ?? false,
  };
}

/** Yield between chunks. In the page this keeps input responsive; in a worker it lets queries interleave. */
function nextTick(): Promise<void> {
  return new Promise((resolve) => {
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    if (idle) idle(() => resolve());
    else setTimeout(resolve, 0);
  });
}

export interface Indexer {
  /** Build the index from the library. Idempotent. */
  seed(): Promise<void>;
  refresh(id: string): Promise<void>;
  /** Run a query, waiting for the build to finish so results are complete. */
  search(query: string, limit?: number): Promise<IndexHit[]>;
  reset(): void;
}

export function createIndexer(): Indexer {
  const index = new SearchIndex();
  let seeding: Promise<void> | null = null;
  // Bumped on reset, so a build that was running for the old library stops
  // adding to the new one.
  let generation = 0;

  async function build(gen: number): Promise<void> {
    const ids = (await db.documents.toCollection().primaryKeys()) as string[];
    for (let i = 0; i < ids.length; i += CHUNK) {
      const docs = await db.documents.bulkGet(ids.slice(i, i + CHUNK));
      if (gen !== generation) return;
      for (const doc of docs) {
        if (doc && doc.deletedAt == null) index.upsert(toIndexed(doc));
      }
      if (i + CHUNK < ids.length) await nextTick();
    }
  }

  return {
    seed() {
      seeding ??= build(generation);
      return seeding;
    },
    async refresh(id) {
      const doc = await db.documents.get(id);
      if (!doc || doc.deletedAt != null) index.remove(id);
      else index.upsert(toIndexed(doc));
    },
    async search(query, limit) {
      await this.seed();
      return index.search(query, limit);
    },
    reset() {
      generation += 1;
      seeding = null;
      index.clear();
    },
  };
}
